<template>
    <div>
        <v-container>
            <v-row>
                <v-col>
                    <v-alert v-if="showBrowserExtensionAlert" prominent dismissible text type="info">
                        <h6 class="text-h6 font-weight-bold">Coaches, Have You Installed Our New Browser Extension?</h6>
                        <p>
                            <span role="img" class="mr-2">🚀</span> Our new
                            <strong>Browser Extension</strong> automatically inputs your meet entries based on your
                            current athlete rosters on Scorsync. The extension will automatically select all of your
                            current athletes and their correct levels. You can also update your club rosters on Scorsync
                            at any time to ensure that your team's information is up-to-date throughout the season!
                        </p>

                        <div class="d-flex">
                            <v-btn
                                href="https://chromewebstore.google.com/detail/scorsync-meet-autofill/ekcgjgeefekofjjhkmlnhnfgndepedde?hl=en"
                                target="_blank"
                                color="info"
                                class="mr-2"
                            >
                                <v-icon>mdi-google-chrome</v-icon>
                                Chrome
                            </v-btn>

                            <v-btn
                                href="https://apps.apple.com/us/app/scorsync-meet-autofill/id6476942818?mt=12"
                                target="_blank"
                                color="info"
                                class="mr-1"
                            >
                                <v-icon>mdi-apple-safari</v-icon>
                                Safari
                            </v-btn>
                        </div>
                    </v-alert>
                </v-col>
            </v-row>

            <v-row v-if="canEditClub && $route().current() === 'clubs.show'">
                <v-col cols="6">
                    <v-checkbox
                        v-model="showingHiddenAthletes"
                        :label="$vuetify.breakpoint.smAndUp ? 'Show Hidden Athletes' : 'Show Hidden'"
                    />
                </v-col>

                <template v-if="$vuetify.breakpoint.smAndUp">
                    <v-col cols="6" class="text-right">
                        <v-btn v-if="!isInEditMode" color="primary" class="mr-2" @click="isInEditMode = !isInEditMode">
                            <v-icon left>mdi-account-edit</v-icon>
                            Edit
                        </v-btn>

                        <v-btn v-else color="primary" text class="mr-2" @click="cancelEdits">
                            <v-icon left>mdi-close</v-icon>
                            Return
                        </v-btn>

                        <v-btn color="success" @click="showAddAthleteModal = true">
                            <v-icon left>mdi-account-plus</v-icon>
                            Add
                        </v-btn>
                    </v-col>
                </template>

                <template v-else>
                    <v-col cols="3" class="text-right my-auto">
                        <v-btn
                            v-if="!isInEditMode"
                            color="primary"
                            class="mr-2"
                            small
                            @click="isInEditMode = !isInEditMode"
                        >
                            <v-icon left>mdi-account-edit</v-icon>
                            Edit
                        </v-btn>

                        <v-btn v-else color="primary" text small class="mr-2" @click="cancelEdits">
                            <v-icon left>mdi-close</v-icon>
                            Return
                        </v-btn>
                    </v-col>
                    <v-col cols="3" class="text-right my-auto">
                        <v-btn color="success" small @click="showAddAthleteModal = true">
                            <v-icon left>mdi-account-plus</v-icon>
                            Add
                        </v-btn>
                    </v-col>
                </template>
            </v-row>

            <v-row v-if="$vuetify.breakpoint.smAndUp">
                <v-col cols="12">
                    <v-data-table
                        v-model="selectedAthletes"
                        :headers="headers"
                        :items="athletes"
                        :search="query"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                        :items-per-page="paginate ? 15 : athletes.length"
                        :server-items-length="paginate ? pagination.total : athletes.length"
                        :page="paginate ? $page.props.pagination.current_page : 1"
                        :loading="loadingAthletes"
                        :show-select="isInEditMode"
                        class="elevation-1"
                        @update:page="changePage"
                        @click:row="linkToAthlete"
                    >
                        <template #[`header.data-table-select`]>
                            <div v-if="isInEditMode && selectedAthletes.length > 0">
                                <v-btn
                                    v-if="hasSelectedHiddenAthletes"
                                    color="success"
                                    @click="restoreSelectedAthletes"
                                >
                                    <v-icon left color="green">mdi-restore</v-icon>
                                    Restore
                                </v-btn>

                                <v-btn v-else :loading="hidingAthletes" color="error" @click="hideSelectedAthletes">
                                    <v-icon left>mdi-delete</v-icon>
                                    Hide
                                </v-btn>
                            </div>
                        </template>

                        <template #[`item.data-table-select`]="{ item: athlete, isSelected, select }">
                            <v-simple-checkbox
                                :value="isSelected"
                                :readonly="canSelectAthlete(athlete)"
                                :disabled="canSelectAthlete(athlete)"
                                @input="select($event)"
                            ></v-simple-checkbox>
                        </template>

                        <template #[`item.first_name`]="{ item: athlete }">
                            <template v-if="$store.state.debugMode"> #{{ athlete.id }}</template>
                            <inertia-link :href="`/athletes/${athlete.id}`">
                                {{ athlete.first_name }}
                            </inertia-link>
                        </template>

                        <template #[`item.last_name`]="{ item: athlete }">
                            <inertia-link :href="`/athletes/${athlete.id}`">
                                {{ athlete.last_name }}
                            </inertia-link>
                        </template>

                        <template v-if="showClub" #[`item.club.name`]="{ item: athlete }">
                            <inertia-link :href="`/clubs/${athlete.club?.id}`">
                                {{ athlete.club?.name }}
                            </inertia-link>
                        </template>

                        <template #[`item.avatar`]="{ item: athlete }">
                            <scorsync-avatar :person="athlete" />
                        </template>

                        <template #[`item.gender`]="{ item: athlete }">
                            {{ athlete.gender.toUpperCase() }}
                        </template>

                        <template #[`item.age`]="{ item: athlete }">
                            {{ athlete.age }}
                        </template>

                        <template #[`item.tra_level`]="{ item: athlete }">
                            <template v-if="isInEditMode">
                                <v-select
                                    v-model="athlete.tra_level"
                                    :items="levels"
                                    :loading="changingLevel === `${athlete.id}-tra`"
                                    item-text="short"
                                    item-value="short"
                                    outlined
                                    dense
                                    class="font-weight-bold mb-n6"
                                    style="max-width: 100px"
                                    @change="changeAthleteLevel(athlete, 'tra')"
                                />
                            </template>

                            <template v-else>
                                <v-chip v-if="!!athlete.tra_level" color="blue" dark class="font-weight-bold">
                                    {{ athlete.tra_level }}
                                </v-chip>
                            </template>
                        </template>

                        <template #[`item.tum_level`]="{ item: athlete }">
                            <template v-if="isInEditMode">
                                <v-select
                                    v-model="athlete.tum_level"
                                    :items="levels"
                                    :loading="changingLevel === `${athlete.id}-tum`"
                                    item-text="short"
                                    item-value="short"
                                    outlined
                                    dense
                                    class="font-weight-bold mb-n6"
                                    style="max-width: 100px"
                                    @change="changeAthleteLevel(athlete, 'tum')"
                                />
                            </template>

                            <template v-else>
                                <v-chip v-if="!!athlete.tum_level" color="red" dark class="font-weight-bold">
                                    {{ athlete.tum_level }}
                                </v-chip>
                            </template>
                        </template>

                        <template #[`item.dmt_level`]="{ item: athlete }">
                            <template v-if="isInEditMode">
                                <v-select
                                    v-model="athlete.dmt_level"
                                    :items="levels"
                                    :loading="changingLevel === `${athlete.id}-dmt`"
                                    item-text="short"
                                    item-value="short"
                                    outlined
                                    dense
                                    class="font-weight-bold mb-n6"
                                    style="max-width: 100px"
                                    @change="changeAthleteLevel(athlete, 'dmt')"
                                />
                            </template>

                            <template v-else>
                                <v-chip v-if="!!athlete.dmt_level" color="green" dark class="font-weight-bold">
                                    {{ athlete.dmt_level }}
                                </v-chip>
                            </template>
                        </template>

                        <template #[`item.last_competed_at`]="{ item: athlete }">
                            <v-chip>
                                <datetime-display
                                    v-if="athlete.last_competed_at"
                                    :datetime="athlete.last_competed_at"
                                    format="MMM YYYY"
                                />
                                <span v-else>N/A</span>
                            </v-chip>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>

            <v-row v-else>
                <v-col cols="12">
                    <div v-if="$vuetify.breakpoint.smAndUp && isInEditMode && selectedAthletes.length > 0">
                        <v-btn v-if="hasSelectedHiddenAthletes" color="success" @click="restoreSelectedAthletes">
                            <v-icon left color="green">mdi-restore</v-icon>
                            Restore
                        </v-btn>

                        <v-btn v-else :loading="hidingAthletes" color="error" @click="hideSelectedAthletes">
                            <v-icon left>mdi-delete</v-icon>
                            Hide
                        </v-btn>
                    </div>

                    <v-card
                        v-for="athlete in athletes"
                        :key="athlete?.id"
                        class="elevation-2 my-3 mx-3 rounded-lg"
                        @click="linkToAthlete(athlete)"
                    >
                        <v-card-title class="py-1">
                            <v-simple-checkbox
                                v-if="!$vuetify.breakpoint.smAndUp && isInEditMode"
                                :value="selectedAthletes.includes(athlete)"
                                :readonly="canSelectAthlete(athlete)"
                                :disabled="canSelectAthlete(athlete)"
                                class="mt-1"
                                @input="mobileSelectAthlete(athlete)"
                            ></v-simple-checkbox>

                            <v-avatar size="60">
                                <scorsync-avatar :person="athlete" />
                            </v-avatar>

                            <div>
                                <div class="mb-0" style="line-height: 1rem">
                                    <div v-if="isInEditMode">
                                        {{ athlete.name }}
                                        <span v-if="athlete.is_birthday" role="img" aria-label="Birthday cake">
                                            🎂
                                        </span>
                                    </div>

                                    <inertia-link v-else :href="`/athletes/${athlete.id}`">
                                        {{ athlete.name }}
                                        <span v-if="athlete.is_birthday" role="img" aria-label="Birthday cake">
                                            🎂
                                        </span>
                                    </inertia-link>
                                </div>
                                <div v-if="$route().current() !== 'clubs.show'" class="grey--text caption mt-0">
                                    <inertia-link :href="`/clubs/${athlete.club_id}`">
                                        {{ athlete.club?.name }}
                                    </inertia-link>
                                </div>
                            </div>

                            <v-spacer />

                            <div class="text-right">
                                <span class="font-weight-bold">
                                    {{ athlete.gender.toUpperCase() }} {{ athlete.age }} yrs
                                </span>
                            </div>
                        </v-card-title>

                        <v-card-text>
                            <div class="d-flex flex-row justify-space-between mt-2">
                                <div>
                                    TRA
                                    <v-select
                                        v-if="isInEditMode"
                                        v-model="athlete.tra_level"
                                        :items="levels"
                                        :loading="changingLevel === `${athlete.id}-tra`"
                                        item-text="short"
                                        item-value="short"
                                        color="blue"
                                        outlined
                                        dense
                                        class="font-weight-bold mb-n6"
                                        style="max-width: 100px"
                                        @change="changeAthleteLevel(athlete, 'tra')"
                                    />

                                    <v-chip
                                        v-else-if="!!athlete.tra_level"
                                        color="blue"
                                        dark
                                        class="ml-3 font-weight-bold"
                                    >
                                        {{ athlete.tra_level }}
                                    </v-chip>
                                </div>

                                <div>
                                    DMT

                                    <v-select
                                        v-if="isInEditMode"
                                        v-model="athlete.dmt_level"
                                        :items="levels"
                                        :loading="changingLevel === `${athlete.id}-dmt`"
                                        item-text="short"
                                        item-value="short"
                                        color="green"
                                        outlined
                                        dense
                                        class="font-weight-bold mb-n6"
                                        style="max-width: 100px"
                                        @change="changeAthleteLevel(athlete, 'dmt')"
                                    />
                                    <v-chip
                                        v-else-if="!!athlete.dmt_level"
                                        color="green"
                                        dark
                                        class="ml-3 font-weight-bold"
                                    >
                                        {{ athlete.dmt_level }}
                                    </v-chip>
                                </div>

                                <div>
                                    TUM
                                    <v-select
                                        v-if="isInEditMode"
                                        v-model="athlete.tum_level"
                                        :items="levels"
                                        :loading="changingLevel === `${athlete.id}-tum`"
                                        item-text="short"
                                        item-value="short"
                                        color="red"
                                        outlined
                                        dense
                                        class="font-weight-bold mb-n6"
                                        style="max-width: 100px"
                                        @change="changeAthleteLevel(athlete, 'tum')"
                                    />
                                    <v-chip
                                        v-else-if="!!athlete.tum_level"
                                        color="red"
                                        dark
                                        class="ml-3 font-weight-bold"
                                    >
                                        {{ athlete.tum_level }}
                                    </v-chip>
                                </div>
                            </div>
                            <div class="mt-3">
                                Last competed
                                <datetime-display
                                    v-if="athlete.last_competed_at"
                                    :datetime="athlete.last_competed_at"
                                    format="MMM YYYY"
                                />
                                <span v-else>N/A</span>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <v-snackbar
            v-model="athleteChangeNotification"
            :timeout="3000"
            :color="athleteChangeNotificationMessage?.includes('Successfully') ? 'primary' : 'error'"
        >
            {{ athleteChangeNotificationMessage }}
        </v-snackbar>

        <v-dialog v-model="showAddAthleteModal" max-width="600px">
            <add-athlete-modal :club="club" @added="athleteAdded" @close="showAddAthleteModal = false" />
        </v-dialog>

        <v-snackbar v-model="showAthleteAdded" :timeout="5000" top centered color="green">
            {{ showAthleteAddedMessage }}
            <v-btn color="default" text @click="showAthleteAdded = false">Close</v-btn>
        </v-snackbar>
    </div>
</template>

<script>
import Athlete from '@/Models/Athlete.js';
import ScorsyncAvatar from '@/Components/ScorsyncAvatar.vue';
import User from '@/Models/User.js';
import Club from '@/Models/Club.js';
import levels from '@/levels.js';
import AddAthleteModal from '@/Components/Modals/AddAthleteModal.vue';
import DatetimeDisplay from '@/Components/DatetimeDisplay.vue';

export default {
    name: 'AthletesTable',

    components: { DatetimeDisplay, AddAthleteModal, ScorsyncAvatar },

    props: {
        club: {
            type: Club,
            required: false,
            default: null,
        },

        paginate: {
            type: Boolean,
            required: false,
            default: true,
        },

        pagination: {
            type: Object,
            required: false,
            default: () => ({
                current_page: 1,
                last_page: 1,
                total: 0,
            }),
        },

        endpoint: {
            type: String,
            required: false,
            default: '/athletes',
        },

        showClub: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data: () => ({
        levels,

        headers: null,
        sortBy: 'first_name',
        sortDesc: false,
        query: null,
        isInEditMode: false,
        selectedAthletes: [],
        showingHiddenAthletes: false,
        loadingAthletes: false,
        hidingAthletes: false,
        restoringAthletes: false,
        changingLevel: null,
        athleteChangeNotification: false,
        athleteChangeNotificationMessage: null,

        showAddAthleteModal: false,
        showAthleteAdded: false,
        showAthleteAddedMessage: null,
    }),

    computed: {
        user() {
            if (!this.$page.props.auth.user) {
                return null;
            }

            return User.query().with('affiliations').find(this.$page.props.auth.user.id);
        },

        canEditClub() {
            if (!this.club) {
                return false;
            }

            return this.user?.canEditClub(this.club);
        },

        athletes() {
            let orderBy = this.sortBy || 'first_name';
            const orderDirection = this.sortDesc ? 'desc' : 'asc';

            if (['tra_level', 'tum_level', 'dmt_level'].includes(orderBy)) {
                orderBy = `${orderBy}_key`;
            }

            if (this.club?.athletes) {
                return Athlete.query()
                    .where('club_id', this.club.id)
                    .where(athlete => {
                        if (this.showingHiddenAthletes) {
                            return true;
                        }

                        return athlete.hidden_from_roster_at === null;
                    })
                    .orderBy(orderBy, orderDirection)
                    .get();
            }

            return Athlete.query()
                .with('club')
                .whereIdIn(this.pagination.data.map(athlete => athlete.id))
                .orderBy(orderBy, orderDirection)
                .get();
        },

        hasSelectedHiddenAthletes() {
            return this.selectedAthletes.filter(athlete => athlete.hidden_from_roster_at !== null).length > 0;
        },

        showBrowserExtensionAlert() {
            if (!this.$page.props.auth.user) {
                return false;
            }

            const roleNames = this.$page.props.auth.user.roles.map(role => role.name);

            return (
                roleNames.includes('Coach') ||
                roleNames.includes('Admin') ||
                roleNames.includes('Committee Member') ||
                roleNames.includes('Mobility Coach') ||
                roleNames.includes('Mobility Judge') ||
                roleNames.includes('Videographer')
            );
        },
    },

    watch: {
        pagination: {
            handler() {
                if (!this.club?.athletes) {
                    Athlete.insert({ data: this.$page.props.pagination.data });
                }
            },

            immediate: true,
        },

        showingHiddenAthletes(show) {
            if (show) {
                this.loadingAthletes = true;

                Athlete.api().get(`/api/clubs/${this.club.id}/athletes?trashed=only`);

                this.loadingAthletes = false;
            }
        },
    },

    created() {
        this.query = this.$routeParams.search;

        this.headers = [
            {
                text: '',
                sortable: false,
                value: 'avatar',
            },
            {
                text: 'First Name',
                sortable: true,
                value: 'first_name',
            },
            {
                text: 'Last Name',
                sortable: true,
                value: 'last_name',
            },
        ];

        if (this.showClub) {
            this.headers.push({
                text: 'Club',
                sortable: true,
                value: 'club.name',
            });
        }

        this.headers = [
            ...this.headers,

            {
                text: 'Gender',
                sortable: true,
                value: 'gender',
            },
            {
                text: 'Age',
                sortable: true,
                value: 'age',
            },
            {
                text: 'TRA',
                sortable: true,
                value: 'tra_level',
            },
            {
                text: 'TUM',
                sortable: true,
                value: 'tum_level',
            },
            {
                text: 'DMT',
                sortable: true,
                value: 'dmt_level',
            },
            {
                text: 'Last Competed',
                sortable: true,
                value: 'last_competed_at',
            },
        ];
    },

    methods: {
        changePage(page) {
            const searchParams = new URLSearchParams(window.location.search);

            searchParams.set('page', page);

            const queryString = searchParams.toString();

            this.$inertia.visit(`${this.endpoint}?${queryString}`, {
                only: ['pagination'],
                preserveScroll: true,
                replace: true,
                preserveState: true,
            });
        },

        async updateAthlete(athlete, blob) {
            const formData = new FormData();

            formData.append('_method', 'put');
            formData.append('avatar', blob, 'avatar.png');
            formData.append('model', 'athlete');

            await Athlete.api().post(`/api/avatar/${athlete.id}`, formData);
        },

        linkToAthlete(athlete) {
            if (this.isInEditMode) {
                return;
            }

            this.$inertia.visit(`/athletes/${athlete.id}`);
        },

        cancelEdits() {
            this.isInEditMode = !this.isInEditMode;
        },

        restoreSelectedAthletes() {
            this.restoringAthletes = true;

            this.selectedAthletes.forEach(athlete => (athlete.hidden_from_roster_at = null));

            Athlete.api().post(`/api/clubs/${this.club.id}/athletes`, {
                athletes: this.selectedAthletes,
            });

            this.selectedAthletes = [];

            this.restoringAthletes = false;
        },

        async hideSelectedAthletes() {
            this.hidingAthletes = true;

            this.selectedAthletes.forEach(athlete => {
                athlete.hidden_from_roster_at = new Date();
            });

            await Athlete.api().post(`/api/clubs/${this.club.id}/athletes`, {
                athletes: this.selectedAthletes,
            });

            this.selectedAthletes = [];

            this.hidingAthletes = false;
        },

        async changeAthleteLevel(athlete, event) {
            try {
                this.changingLevel = `${athlete.id}-${event}`;
                await Athlete.api().post(`/api/clubs/${this.club.id}/athletes`, { athletes: [athlete] });

                this.athleteChangeNotification = true;
                this.athleteChangeNotificationMessage = `Successfully updated ${
                    athlete.name
                }'s ${event.toUpperCase()} level.`;
            } catch (e) {
                console.error(e);
                this.athleteChangeNotification = true;
                this.athleteChangeNotificationMessage = `Failed to update ${
                    athlete.name
                }'s ${event.toUpperCase()} level.`;
            } finally {
                this.changingLevel = null;
            }
        },

        canSelectAthlete(athlete) {
            return Boolean(
                this.selectedAthletes.length > 0 &&
                    ((this.hasSelectedHiddenAthletes && !athlete.hidden_from_roster_at) ||
                        (!this.hasSelectedHiddenAthletes && athlete.hidden_from_roster_at)),
            );
        },

        mobileSelectAthlete(athlete) {
            if (this.canSelectAthlete(athlete)) {
                return;
            }

            if (this.selectedAthletes.includes(athlete)) {
                this.selectedAthletes = this.selectedAthletes.filter(selectedAthlete => selectedAthlete !== athlete);
            } else {
                this.selectedAthletes.push(athlete);
            }
        },

        athleteAdded(athlete) {
            this.showAthleteAdded = true;
            this.showAthleteAddedMessage = `${athlete.first_name} ${athlete.last_name} has been added to the club.`;
            this.showAddAthleteModal = false;
        },
    },
};
</script>
